import {Inject, Injectable} from '@angular/core';
import {AsyncSubject, Observable, of, Subject} from 'rxjs';
import {UnitStatusModel} from './unit-status.model';
import {HttpClient} from '@angular/common/http';
import {catchError, map, switchMap, withLatestFrom} from 'rxjs/operators';
import {ActionOnChangeUnitStatus} from './action-on-change-unit-status';
import {ModalWindowType} from '../shared/modal-window/modal-window-type';
import {ModalWindowResponse} from '../shared/modal-window/modal-window-response';
import {ModalWindowService} from '../shared/modal-window/modal-window.service';
import {TranslateService} from '@ngx-translate/core';
import {NotificationsService} from 'angular2-notifications';
import {Unit} from '../dashboard/monitoring/unit';
import {DashboardUnitCacheService} from '../dashboard/monitoring/services/dashboard-unit-cache.service';
import {DashboardSelectedUnitCacheService} from '../dashboard/monitoring/services/dashboard-selected-unit-cache.service';

@Injectable({
  providedIn: 'root'
})
export class UnitStatusManagerService {
  constructor(
    protected httpClient: HttpClient,
    protected modalWindowService: ModalWindowService,
    protected translates: TranslateService,
    protected notif: NotificationsService,
    protected dashboardUnitCacheService: DashboardUnitCacheService,
    protected dashboardSelectedUnitCacheService: DashboardSelectedUnitCacheService,
    @Inject('ENVIRONMENT_VERSION') protected environmentVersion: string,
    @Inject('ENVIRONMENT_API_BASEPOINT') protected environmentApiBasepoint: string,
  ) {
    this.httpClient.get(this.environmentApiBasepoint + 'unit-status', {observe: 'body'})
      .subscribe((data: Array<UnitStatusModel>) => {
        this.availableStatuses.next(
          data.reduce(
            (prev, status) => {
              if (!(status.unit_type_name in prev)) {
                prev[status.unit_type_name] = {};
              }
              prev[status.unit_type_name][status.id] = status;
              return prev;
            },
            {}
          )
        );
        this.availableStatuses.complete();
      });
  }

  private readonly commandMaintenanceOff = 'maintenance_off';
  private readonly commandMaintenanceOn = 'maintenance_on';
  public unitStatusChangeEvent: Subject<{unitId: number, newStatus: UnitStatusModel, completed: boolean}>
    = new Subject<{unitId: number, newStatus: UnitStatusModel, completed: boolean}>();

  availableStatuses: AsyncSubject<{[id: string]: UnitStatusModel}> = new AsyncSubject<{[p: string]: UnitStatusModel}>();
  public getActionOnChangeStatus(unitType: string, oldStatusId: number, newStatusId: number): Observable<ActionOnChangeUnitStatus> {
    return this.availableStatuses.pipe(
      map(
        availableStatuses => {
          const result: ActionOnChangeUnitStatus = {
            sendCommandDisableServiceMode: false,
            sendCommandEnableServiceMode: false,
            showUnitInApplication: false,
            hideUnitFromApplication: false,
          };
          if ((unitType in availableStatuses)
            && (oldStatusId in availableStatuses[unitType])
            && (newStatusId in availableStatuses[unitType])) {
            const oldStatus = availableStatuses[unitType][oldStatusId];
            const newStatus = availableStatuses[unitType][newStatusId];
            if (oldStatus.service_mode && !newStatus.service_mode) {
              result.sendCommandDisableServiceMode = true;
            }
            if (!oldStatus.service_mode && newStatus.service_mode) {
              result.sendCommandEnableServiceMode = true;
            }
            if (oldStatus.show_in_application && !newStatus.show_in_application) {
              result.hideUnitFromApplication = true;
            }
            if (!oldStatus.show_in_application && newStatus.show_in_application) {
              result.showUnitInApplication = true;
            }
          }
          return result;
        }
      )
    );
  }
  public getStatusesForUnitType(unitTypeName: string): Observable<Array<UnitStatusModel>> {
    return this.availableStatuses.pipe(
      map(
        availableStatuses => Object.values(availableStatuses[unitTypeName])
      )
    );
  }
  public needToChangeStatusWhenSendingCommand(unitType: string, oldStatusId: number, commandName: string):
    Observable<Array<UnitStatusModel> | false>  {
    return this.availableStatuses.pipe(
      map(
        availableStatuses => {
          if (commandName !== this.commandMaintenanceOn && commandName !== this.commandMaintenanceOff) {
            return false;
          }
          const oldStatus = availableStatuses[unitType][oldStatusId];
          if (oldStatus.service_mode && commandName === this.commandMaintenanceOff) {
            return Object.values(availableStatuses[unitType]).filter(status => !status.service_mode);
          }
          if (!oldStatus.service_mode && commandName === this.commandMaintenanceOn) {
            return Object.values(availableStatuses[unitType]).filter(status => status.service_mode);
          }
          return false;
        }
      )
    );
  }

  protected updateCache(unitId: number, status: UnitStatusModel) {
    this.dashboardUnitCacheService.updateUnitStatus(unitId, status);
    this.dashboardSelectedUnitCacheService.updateUnitStatus(unitId, status);
  }
  public getStatusToShowHideUnitFromMobileApplication(
    unit: Unit,
    newStateIsShow: boolean
  ): Observable<UnitStatusModel> {
    return this.getStatusesForUnitType(unit.unitModel.unit_type_name).pipe(
      map(
        statuses => newStateIsShow ?
            statuses.find(status => status.show_in_application)
            : statuses.find(status => !status.show_in_application && !status.service_mode)
      )
    );
  }
  public changeStatusToShowHideUnitFromMobileApplication(
    unit: Unit,
    newStateIsShow: boolean
  ): Observable<UnitStatusModel> {
    const oldStatusId = unit.unit_status_id;
    return this.getStatusesForUnitType(unit.unitModel.unit_type_name).pipe(
      switchMap(
        statuses => {
          const oldStatus = statuses.find(status => status.id === oldStatusId);
          const newStatus = newStateIsShow ?
            statuses.find(status => status.show_in_application)
            : statuses.find(status => !status.show_in_application && !status.service_mode);
          this.updateCache(unit.id, newStatus);
          return this.changeStatusRequest(unit.id, newStatus.id).pipe(
            map(
              result => {
                if (result && result.success) {
                  return newStatus;
                } else {
                  this.updateCache(unit.id, oldStatus);
                  return oldStatus;
                }
              }
            )
          );
        }
      )
    );
  }
  protected changeStatusRequest(unitId: number, newStatusId: number) {
    const requestBody = {unit_status_id: newStatusId, unit_id: unitId};
    return this.httpClient.post(
      this.environmentApiBasepoint + 'change-unit-status',
      requestBody
    ).pipe(
      catchError(
        errorResponse => {
          switch (errorResponse.status) {
            case 500:
              this.notif.error(this.translates.instant('Server error'));
              console.error(errorResponse.error);
              break;
            case 422:
              let processed = false;
              (errorResponse.error as Array<any>).forEach(
                item => {
                  if (item.message === 'Cannot change status because unit\'s owner is blocked') {
                    this.notif.error(this.translates.instant('Cannot change status because unit\'s owner is blocked'));
                    processed = true;
                  }
                }
              );
              if (!processed) {
                this.notif.error(this.translates.instant('Operation not permitted'));
              }
              break;
            default:
              this.notif.error(this.translates.instant('Unknown server error'));
              console.error(errorResponse.error);
          }
          return of(null);
        }
      )
    );
  }

  public changeStatus(
    unitId: number,
    unitTypeName: string,
    oldStatusId: number,
    newStatusId: number,
    confirmationNeeded = true
  ): Observable<UnitStatusModel> {
    return this.getActionOnChangeStatus(unitTypeName, oldStatusId, newStatusId)
      .pipe(
        switchMap(
          actionOnChangeStatus => {
            const confirmationList: Array<string> = [];
            if (actionOnChangeStatus.sendCommandEnableServiceMode) {
              confirmationList.push('Send command "Enable service mode"');
            }
            if (actionOnChangeStatus.sendCommandDisableServiceMode) {
              confirmationList.push('Send command "Disable service mode"');
            }
            if (actionOnChangeStatus.showUnitInApplication) {
              confirmationList.push('Show unit in application');
            }
            if (actionOnChangeStatus.hideUnitFromApplication) {
              confirmationList.push('Hide unit in application');
            }
            return confirmationNeeded && confirmationList.length > 0 ?
              this.modalWindowService.showWindow({
                modalWindowType: ModalWindowType.Confirmation,
                text: this.translates.instant('Unit status change'),
                confirmationList: confirmationList
              })
              : of({confirmed: true, checklistResult: {}} as ModalWindowResponse);
            // const checklist: Array<ModalWindowCheckbox> = [];
            // if (actionOnChangeStatus.sendCommandEnableServiceMode) {
            //   checklist.push({
            //     name: 'enableSendingCommandOnStatusChange',
            //     label: 'Send command "Enable service mode"',
            //     value: true
            //   });
            // }
            // if (actionOnChangeStatus.sendCommandDisableServiceMode) {
            //   checklist.push({
            //     name: 'enableSendingCommandOnStatusChange',
            //     label: 'Send command "Disable service mode"',
            //     value: true
            //   });
            // }
            // if (actionOnChangeStatus.showUnitInApplication) {
            //   checklist.push({
            //     name: 'enableAlteringVisibilityInApplicationOnStatusChange',
            //     label: 'Show unit in application',
            //     value: true
            //   });
            // }
            // if (actionOnChangeStatus.hideUnitFromApplication) {
            //   checklist.push({
            //     name: 'enableAlteringVisibilityInApplicationOnStatusChange',
            //     label: 'Hide unit in application',
            //     value: true
            //   });
            // }
            // return checklist.length > 0 ?
            //   this.modalWindowService.showWindow({
            //     modalWindowType: ModalWindowType.Checklist,
            //     text: this.translates.instant('Unit status change'),
            //     checklist: checklist
            //   })
            //   : of({confirmed: true, checklistResult: {}} as ModalWindowResponse);
          }),
        switchMap(
          response => {
            if (response.confirmed) {
              const requestBody = {unit_status_id: newStatusId};
              return this.changeStatusRequest(unitId, newStatusId);
            } else {
              return of(false);
            }
          }
        ),
        withLatestFrom(this.getStatusesForUnitType(unitTypeName)),
        map(
          ([result, statuses]) => {
            if (result && result.success) {
              return statuses.find(status => status.id === newStatusId);
            } else {
              return statuses.find(status => status.id === oldStatusId);
            }
          }
        )
      );
  }
}

